import { addWorkingDays, countWorkingDays, nearestWorkingDay } from '../calendar/working-days.js';
import type { HolidayRegion } from '../calendar/holidays.js';

// Dependency constraints (GANTT-04, GANTT-07). Dates are inclusive business dates: a task
// occupies its due date. Lag counts working days and may be negative.
//
//   FS: successor starts after the predecessor's due date  start ≥ due + (1 + lag) working days
//   SS: successor starts with the predecessor              start ≥ start + lag
//   FF: successor finishes with the predecessor            due   ≥ due + lag
//   SF: successor finishes before the predecessor starts   due   ≥ start + (lag − 1)
//
// With lag 0, FS and SF leave no gap and no overlap; SS and FF share the day.

export type DependencyType = 'FS' | 'SS' | 'FF' | 'SF';

export interface DateRange {
  startDate: string;
  dueDate: string;
}

export interface DependencyBound {
  /** Field of the successor the dependency constrains. */
  field: 'startDate' | 'dueDate';
  /** Earliest allowed value (a working day). */
  earliest: string;
}

export function dependencyBound(
  predecessor: DateRange,
  type: DependencyType,
  lagDays: number,
  region: HolidayRegion,
): DependencyBound {
  const at = (date: string, days: number) =>
    nearestWorkingDay(addWorkingDays(date, days, region), region);
  switch (type) {
    case 'FS':
      return { field: 'startDate', earliest: at(predecessor.dueDate, 1 + lagDays) };
    case 'SS':
      return { field: 'startDate', earliest: at(predecessor.startDate, lagDays) };
    case 'FF':
      return { field: 'dueDate', earliest: at(predecessor.dueDate, lagDays) };
    case 'SF':
      return { field: 'dueDate', earliest: at(predecessor.startDate, lagDays - 1) };
  }
}

export function satisfiesBound(range: DateRange, bound: DependencyBound): boolean {
  return range[bound.field] >= bound.earliest;
}

/** Duration in working days, at least 1 (a task on a non-working day still lasts one day). */
export function workingDuration(range: DateRange, region: HolidayRegion): number {
  return Math.max(1, countWorkingDays(range.startDate, range.dueDate, region));
}

/**
 * Moves `range` later so that it satisfies `bound`, keeping its duration in working days.
 * Milestones (one date) move as a whole.
 */
export function shiftToBound(
  range: DateRange,
  bound: DependencyBound,
  milestone: boolean,
  region: HolidayRegion,
): DateRange {
  if (satisfiesBound(range, bound)) return range;
  if (milestone) return { startDate: bound.earliest, dueDate: bound.earliest };
  const span = workingDuration(range, region) - 1;
  if (bound.field === 'startDate') {
    return { startDate: bound.earliest, dueDate: addWorkingDays(bound.earliest, span, region) };
  }
  return { startDate: addWorkingDays(bound.earliest, -span, region), dueDate: bound.earliest };
}
